import { UserRoles } from '@cloud-wave/neon-common-lib';

import { useAuthContext } from 'lib/core/context/AuthProvider';

import { usePermissionsContext } from 'lib/common/contexts/PermissionsContext';

import { useHistory } from 'lib/common/hooks/useHistory';
import useIsolatedNavigate from 'lib/common/hooks/useIsolatedNavigate';

import Icon from 'lib/common/components/Icon';
import PopoverMenu from 'lib/common/components/PopoverMenu';
import PopoverMenuItem from 'lib/common/components/PopoverMenuItem';

import { SoftphoneNavOption, SoftphoneNavOptionChild } from 'lib/common/types/SoftphoneNavOption';

const MORE_TASKS_MENU_OFFSET = 30;

type TSoftphoneNavMenuFolder = SoftphoneNavOption & {
  childOptions: SoftphoneNavOptionChild[];
  isReversedLayout?: boolean;
  anchor?: React.ReactNode;
  offset?: number;
  offsetLeft?: number;
};

export default function SoftphoneNavMenuFolder({
  icon,
  name,
  childOptions,
  isReversedLayout,
  anchor,
  offset,
  offsetLeft,
}: TSoftphoneNavMenuFolder) {
  const { navigate } = useHistory();
  const { user } = useAuthContext();
  const role = user?.role ?? UserRoles.AGENT;

  const { hasPermission } = usePermissionsContext();

  const isolatedNavigate = useIsolatedNavigate();

  const openLink = ({ link, external, isolated }: { link: string; external?: boolean; isolated?: boolean }) => {
    if (external) {
      return void window.open(link, '_blank');
    }

    if (!isolated) {
      return void navigate(link);
    }

    isolatedNavigate(link);
  };

  const options = childOptions.filter(({ permission, permissionType, requiresRoles }) => {
    const satisfiesRoleRequirements = !requiresRoles || requiresRoles.includes(role);

    if (!satisfiesRoleRequirements) {
      return false;
    }

    if (!permission) {
      return true;
    }

    return hasPermission({ type: permissionType || 'tenant', permission });
  });

  if (!options.length) {
    return null;
  }

  return (
    <PopoverMenu
      anchor={
        anchor || (
          <button
            aria-label="Open app menu"
            className="softphone-nav-menu-item"
            data-testid="softphone-nav-menu-folder-button"
          >
            <Icon tooltip={name} icon={icon} size={18} tooltipPlacement="right" />
          </button>
        )
      }
      offset={offset || 0}
      offsetLeft={offsetLeft ?? (isReversedLayout ? -MORE_TASKS_MENU_OFFSET : MORE_TASKS_MENU_OFFSET)}
      ignoreHorizontalReverse
    >
      {options.map(({ link, icon: childIcon, name, isolated, external }) => (
        <PopoverMenuItem
          data-testid={name}
          key={link}
          onClick={() => openLink({ link, isolated, external })}
          text={name}
          icon={childIcon}
        />
      ))}
    </PopoverMenu>
  );
}
